import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { AuthContext } from "../hooks/useAuth";
import type { AuthContextValue, AuthUser } from "../hooks/useAuth";
import type { EmailVerificationStatus } from "../firebase";
import { getCsrfToken } from "../utils/csrf";

interface AuthProviderProps {
  children: ReactNode;
}

type VerificationError = Error & { code: string };
const GOOGLE_REDIRECT_ERROR =
  "We couldn't complete Google sign-in. Please try again.";

function verificationError(code: string, message: string): VerificationError {
  return Object.assign(new Error(message), { code });
}

function authOperationCancelled(): Error & { code: string } {
  return Object.assign(new Error("Authentication operation was cancelled."), {
    code: "auth/operation-cancelled",
  });
}

async function resendVerificationFromSession(
  returnUrl?: string,
): Promise<EmailVerificationStatus> {
  const csrf = getCsrfToken();
  let response: Response;
  try {
    response = await fetch("/bff/auth/email-verification", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      },
      body: JSON.stringify({ returnUrl }),
    });
  } catch {
    throw verificationError(
      "email-verification/unavailable",
      "Verification email request failed.",
    );
  }

  const body = (await response.json().catch(() => ({}))) as {
    code?: unknown;
    status?: unknown;
  };
  if (
    response.ok &&
    (body.status === "sent" || body.status === "already_verified")
  ) {
    return body.status;
  }
  if (
    response.status === 429 ||
    body.code === "EMAIL_VERIFICATION_RATE_LIMITED"
  ) {
    throw verificationError(
      "auth/too-many-requests",
      "Too many verification email requests.",
    );
  }
  if (
    response.status === 401 &&
    (body.code === "EMAIL_VERIFICATION_SIGN_IN_REQUIRED" ||
      body.code === "EMAIL_VERIFICATION_IDENTITY_INVALID")
  ) {
    throw verificationError(
      "auth/no-current-user",
      "Sign in to request a verification email.",
    );
  }
  throw verificationError(
    "email-verification/unavailable",
    "Verification email is temporarily unavailable.",
  );
}

/**
 * Provides auth context for all 3 apps.
 *
 * On mount: checks BFF session and consumes a returned Google redirect result
 * signIn: Firebase Google redirect → exchange token with BFF → session cookie set
 * signOut: POST /bff/auth/logout → clear cookies → Firebase sign out
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [hasPendingVerificationAuthority, setHasPendingVerificationAuthority] =
    useState(false);
  // A capability exchange can convert this browser into a display while the
  // mount-time `/me` check is still in flight. Its old success response must
  // never restore the ordinary user after that conversion clears the BFF
  // session. Incrementing this generation invalidates every in-flight session
  // observation before local authority is cleared.
  const sessionGeneration = useRef(0);
  // Sign-out must also stop every late session-exchange response from
  // installing a new BFF cookie after the user deliberately ended the local
  // session. Multiple providers can overlap briefly, so retain every active
  // controller rather than only the most recent one.
  const pendingSessionExchanges = useRef(
    new Map<AbortController, Promise<void>>(),
  );

  // Every newer identity intent owns a fresh fence, including transitions
  // between personal and managed-work login. Wait for older mutations (and
  // transient Firebase cleanup) before starting another cookie-producing
  // request: ignoring stale React results alone does not order Set-Cookie.
  const runAuthIntent = useCallback(
    async <T,>(invoke: (generation: number) => Promise<T>): Promise<T> => {
      const generation = ++sessionGeneration.current;
      const previous = [...pendingSessionExchanges.current.entries()];
      for (const [controller] of previous) controller.abort();
      const controller = new AbortController();
      let markSettled!: () => void;
      const settled = new Promise<void>((resolve) => {
        markSettled = resolve;
      });
      pendingSessionExchanges.current.set(controller, settled);
      try {
        await Promise.all(previous.map(([, completion]) => completion));
        if (generation !== sessionGeneration.current)
          throw authOperationCancelled();
        const result = await invoke(generation);
        if (generation !== sessionGeneration.current)
          throw authOperationCancelled();
        return result;
      } finally {
        pendingSessionExchanges.current.delete(controller);
        markSettled();
      }
    },
    [],
  );

  // Exchange a verified identity token for a BFF session and set user state.
  const exchangeToken = useCallback(
    async (idToken: string, shouldApplyResult?: () => boolean) => {
      const generationAtStart = sessionGeneration.current;
      const canApplyResult =
        shouldApplyResult ??
        (() => generationAtStart === sessionGeneration.current);
      const controller = new AbortController();
      let markSettled!: () => void;
      const settled = new Promise<void>((resolve) => {
        markSettled = resolve;
      });
      pendingSessionExchanges.current.set(controller, settled);
      try {
        const response = await fetch("/bff/auth/session", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error ?? "Session creation failed");
        }

        const data = await response.json();
        // A new response is not authority by itself. Only apply it while the
        // caller's session generation is still current (for example, never
        // after a user pressed Sign out while an exchange was in flight).
        if (!canApplyResult()) throw authOperationCancelled();
        setUser({
          uid: data.userId ?? data.uid,
          email: data.email ?? "",
          displayName: data.displayName ?? data.display_name ?? null,
          photoURL: data.photoURL ?? null,
          customClaims: data.customClaims ?? data.custom_claims ?? {},
        });
      } finally {
        pendingSessionExchanges.current.delete(controller);
        markSettled();
      }
    },
    [],
  );

  const clearTransientFirebaseSession = useCallback(async () => {
    try {
      const { signOutFirebase } = await import("../firebase");
      await signOutFirebase();
    } catch (error) {
      // The BFF session is the authoritative web session. If Firebase cleanup
      // fails after exchange, keep the BFF login and warn for investigation.
      console.warn("[Auth] Failed to clear transient Firebase session:", error);
    } finally {
      // This flag represents current authority, not signup history. Once a web
      // session flow deliberately clears Firebase state it must never survive
      // as a fallback after the BFF session later expires.
      setHasPendingVerificationAuthority(false);
    }
  }, []);

  const establishWebSession = useCallback(
    async (getIdToken: () => Promise<string>, operationGeneration: number) => {
      try {
        const idToken = await getIdToken();
        if (operationGeneration !== sessionGeneration.current) {
          throw authOperationCancelled();
        }
        await exchangeToken(
          idToken,
          () => operationGeneration === sessionGeneration.current,
        );
      } finally {
        await clearTransientFirebaseSession();
      }
    },
    [clearTransientFirebaseSession, exchangeToken],
  );

  // Firebase must settle a returned redirect before we read `/me`. Otherwise
  // a still-valid prior BFF cookie can briefly render and navigate as the old
  // user while the browser is completing the new Google identity.
  useEffect(() => {
    let cancelled = false;
    const initialGeneration = sessionGeneration.current;
    const current = (generation: number) =>
      !cancelled && generation === sessionGeneration.current;

    async function checkSession(generation: number) {
      try {
        const response = await fetch("/bff/auth/me", {
          method: "GET",
          credentials: "include",
        });
        if (response.status === 204) {
          if (current(generation)) {
            setUser(null);
            setHasPendingVerificationAuthority(false);
          }
          return;
        }
        if (!response.ok) {
          if (current(generation)) {
            setUser(null);
            setHasPendingVerificationAuthority(false);
          }
          return;
        }
        const data = await response.json();
        if (current(generation)) {
          setUser({
            uid: data.userId ?? data.uid ?? data.user_id,
            email: data.email ?? "",
            displayName: data.displayName ?? data.display_name ?? null,
            photoURL: data.photoURL ?? data.photo_url ?? null,
            customClaims: data.customClaims ?? data.custom_claims ?? {},
          });
        }
      } catch (err) {
        console.error("[Auth] Session check failed:", err);
        if (current(generation)) {
          setUser(null);
          setHasPendingVerificationAuthority(false);
        }
      } finally {
        if (current(generation)) setLoading(false);
      }
    }

    async function initializeSession() {
      let idToken: string | null;
      let redirectWasInitiated = false;
      try {
        const { consumeGoogleRedirectSignIn, hasPendingGoogleRedirectSignIn } =
          await import("../firebase");
        redirectWasInitiated = hasPendingGoogleRedirectSignIn();
        idToken = await consumeGoogleRedirectSignIn();
      } catch (error) {
        if (!current(initialGeneration)) return;
        console.error("[Auth] Google redirect completion failed:", error);
        if (!redirectWasInitiated) {
          // Firebase can fail to initialize before any redirect was started.
          // Preserve a normal existing BFF session in that case; the pending
          // marker distinguishes it from an account-switch return below.
          if (!cancelled) await checkSession(initialGeneration);
          return;
        }

        // A returned redirect may represent a different Google identity. Do
        // not resurrect a prior BFF user behind a failed/cancelled completion:
        // that would navigate the person into the wrong account. They remain
        // signed out on this document and can retry deliberately.
        sessionGeneration.current += 1;
        if (!cancelled) {
          setUser(null);
          setHasPendingVerificationAuthority(false);
          setAuthError(GOOGLE_REDIRECT_ERROR);
        }
        await clearTransientFirebaseSession();
        if (!cancelled) setLoading(false);
        return;
      }
      if (!current(initialGeneration)) return;
      if (!idToken) {
        if (redirectWasInitiated) {
          // Firebase has consumed the local pending marker but did not return
          // an identity. Treat this as a failed/cancelled account switch, not
          // as permission to restore a previous BFF user behind the login UI.
          sessionGeneration.current += 1;
          setUser(null);
          setHasPendingVerificationAuthority(false);
          setAuthError(GOOGLE_REDIRECT_ERROR);
          await clearTransientFirebaseSession();
          if (!cancelled) setLoading(false);
          return;
        }
        await checkSession(initialGeneration);
        return;
      }

      // Mark the new identity before the exchange so every older observation
      // is stale even if a future caller starts a session check concurrently.
      sessionGeneration.current += 1;
      const redirectGeneration = sessionGeneration.current;
      try {
        await exchangeToken(idToken, () => current(redirectGeneration));
      } catch (error) {
        if (current(redirectGeneration)) {
          console.error(
            "[Auth] Google redirect session exchange failed:",
            error,
          );
          setUser(null);
          setHasPendingVerificationAuthority(false);
          setAuthError(GOOGLE_REDIRECT_ERROR);
        }
      } finally {
        await clearTransientFirebaseSession();
      }
      if (cancelled) return;
      if (current(redirectGeneration)) {
        setLoading(false);
      }
    }

    void initializeSession();
    return () => {
      cancelled = true;
    };
  }, [clearTransientFirebaseSession, exchangeToken]);

  const signIn = useCallback(
    () =>
      runAuthIntent(async (operationGeneration) => {
        setAuthError(null);
        const { beginGoogleRedirectSignIn } = await import("../firebase");
        if (operationGeneration !== sessionGeneration.current) {
          throw authOperationCancelled();
        }
        await beginGoogleRedirectSignIn();
      }),
    [runAuthIntent],
  );

  const signInWithEmailFn = useCallback(
    (email: string, password: string) =>
      runAuthIntent(async (operationGeneration) => {
        setAuthError(null);
        const { signInWithEmail } = await import("../firebase");
        if (operationGeneration !== sessionGeneration.current) {
          throw authOperationCancelled();
        }
        await establishWebSession(
          () => signInWithEmail(email, password),
          operationGeneration,
        );
      }),
    [establishWebSession, runAuthIntent],
  );

  const updateAccountNameFn = useCallback(
    (name: string, password: string) =>
      runAuthIntent(async (generation) => {
        const account = user;
        const csrfToken = getCsrfToken();
        if (
          !account?.uid ||
          !account.email ||
          !csrfToken
        ) {
          throw verificationError(
            "auth/account-changed",
            "Personal session required",
          );
        }
        const assertCurrent = () => {
          if (
            generation !== sessionGeneration.current ||
            getCsrfToken() !== csrfToken
          ) {
            throw verificationError("auth/account-changed", "Account changed");
          }
        };
        const checkSession = async () => {
          assertCurrent();
          const response = await fetch("/bff/auth/session/check", {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify({ expectedUserId: account.uid }),
          });
          if (!response.ok)
            throw verificationError("auth/account-changed", "Account changed");
          assertCurrent();
        };
        try {
          await checkSession();
          const { updateAccountName } = await import("../firebase");
          await updateAccountName(
            account.uid,
            account.email,
            password,
            name,
            checkSession,
            assertCurrent,
          );
          // Profile-only: never mint cookies or update the session-visible user.
          // KV reads cannot atomically fence a different login in another tab.
          // The user explicitly signs in later through the ordinary login flow.
        } finally {
          await clearTransientFirebaseSession();
        }
      }),
    [user, runAuthIntent, clearTransientFirebaseSession],
  );

  const signUpWithEmailFn = useCallback(
    (
      email: string,
      password: string,
      displayName?: string,
      returnUrl?: string,
    ) =>
      runAuthIntent(async (operationGeneration) => {
        setAuthError(null);
        const { signUpWithEmail } = await import("../firebase");
        try {
          const result = await signUpWithEmail(
            email,
            password,
            displayName,
            returnUrl,
          );
          if (operationGeneration !== sessionGeneration.current) {
            throw authOperationCancelled();
          }
          await exchangeToken(
            result.idToken,
            () => operationGeneration === sessionGeneration.current,
          );
          return result.deliveryStatus;
        } finally {
          await clearTransientFirebaseSession();
        }
      }),
    [clearTransientFirebaseSession, exchangeToken, runAuthIntent],
  );

  // #1307: sign-up WITHOUT establishing a web session. signUpWithEmail creates
  // the account + sends the verification email and leaves the new user as the
  // Firebase current-user; deliberately NOT wrapped in establishWebSession, so
  // no BFF session is minted (the app's auth state is BFF-session-based → the
  // user stays unauthenticated) and the transient Firebase session is NOT
  // cleared (so resendVerificationEmail can target the same user). The caller
  // routes to the "verify your email" screen. Returning sign-in is unchanged.
  const signUpPendingVerificationFn = useCallback(
    (
      email: string,
      password: string,
      displayName?: string,
      returnUrl?: string,
    ) =>
      runAuthIntent(async (operationGeneration) => {
        setAuthError(null);
        const { signUpWithEmail } = await import("../firebase");
        const result = await signUpWithEmail(
          email,
          password,
          displayName,
          returnUrl,
        );
        if (operationGeneration !== sessionGeneration.current) {
          await clearTransientFirebaseSession();
          throw authOperationCancelled();
        }
        setHasPendingVerificationAuthority(true);
        return result.accountNameIncomplete
          ? {
              deliveryStatus: result.deliveryStatus,
              accountNameIncomplete: true as const,
            }
          : result.deliveryStatus;
      }),
    [clearTransientFirebaseSession, runAuthIntent],
  );

  const resendVerificationEmailFn = useCallback(
    async (returnUrl?: string) => {
      if (user) {
        try {
          return await resendVerificationFromSession(returnUrl);
        } catch (error) {
          if (
            typeof error === "object" &&
            error !== null &&
            "code" in error &&
            error.code === "auth/no-current-user"
          ) {
            setUser(null);
          }
          throw error;
        }
      }

      const { resendVerificationEmail } = await import("../firebase");
      try {
        return await resendVerificationEmail(returnUrl);
      } catch (error) {
        if (
          typeof error === "object" &&
          error !== null &&
          "code" in error &&
          error.code === "auth/no-current-user"
        ) {
          setHasPendingVerificationAuthority(false);
        }
        throw error;
      }
    },
    [user],
  );

  const resetPasswordFn = useCallback(async (email: string) => {
    setAuthError(null);
    const { resetPassword } = await import("../firebase");
    await resetPassword(email);
  }, []);

  const signOut = useCallback(() => {
    // Make a concurrent `/me` result stale before clearing the local user. This
    // is required for shared venue-display browsers, whose BFF session is
    // intentionally replaced by a device capability mid-mount.
    // The page must become signed out immediately, but the server-side logout
    // waits for a just-started session exchange to settle. Otherwise a late
    // `/session` Set-Cookie could arrive after `/logout` and recreate the
    // browser session. Local authority is already cleared above, so favor the
    // strict ordering guarantee over a timeout that could reopen this race.
    setUser(null);
    setHasPendingVerificationAuthority(false);
    setAuthError(null);
    setLoading(false);
    return runAuthIntent(async () => {
      // Read after the exchange fence: if a completed exchange refreshed the
      // CSRF cookie, logout must authorize against that newest session rather
      // than sending the stale pre-exchange token.
      const csrf = getCsrfToken();

      try {
        // 1. Clear BFF session
        await fetch("/bff/auth/logout", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(csrf ? { "X-CSRF-Token": csrf } : {}),
          },
        });
      } finally {
        // A local sign-out must be authoritative even if the network request
        // fails. This also handles display exchange: the BFF already cleared the
        // ordinary cookie, but this tab must clear Firebase and React state too.
        await clearTransientFirebaseSession();
        setUser(null);
        setHasPendingVerificationAuthority(false);
        setAuthError(null);
        setLoading(false);
      }
    });
  }, [clearTransientFirebaseSession, runAuthIntent]);

  const value = useMemo<
    AuthContextValue & { canResendVerificationEmail: boolean }
  >(
    () => ({
      user,
      loading,
      authError,
      canResendVerificationEmail:
        Boolean(user) || hasPendingVerificationAuthority,
      signIn,
      signInWithEmail: signInWithEmailFn,
      updateAccountName: updateAccountNameFn,
      signUpWithEmail: signUpWithEmailFn,
      signUpPendingVerification: signUpPendingVerificationFn,
      resendVerificationEmail: resendVerificationEmailFn,
      resetPassword: resetPasswordFn,
      signOut,
    }),
    [
      user,
      loading,
      authError,
      hasPendingVerificationAuthority,
      signIn,
      signInWithEmailFn,
      updateAccountNameFn,
      signUpWithEmailFn,
      signUpPendingVerificationFn,
      resendVerificationEmailFn,
      resetPasswordFn,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
